import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import axios from "../../axios";
import { ResetApiState } from "../../redux.type";
import {
  Dashboard,
  DashboardAgent,
  DashboardBank,
  DashboardCategory,
  DashboardConductor,
  ReportStateTypes,
} from "./Dashboard.util";

const extraActions = createExtraActions();

const name = "report/dashboard";

const ReportInitialState: ReportStateTypes = {
  dashboard: ResetApiState(null),
  resetDashboard: ResetApiState(null),

  dashboardCategory: ResetApiState(null),
  resetDashboardCategory: ResetApiState(null),

  dashboardBank: ResetApiState([]),
  resetDashboardBank: ResetApiState(null),

  dashboardConductor: ResetApiState([]),
  resetDashboardConductor: ResetApiState(null),

  dashboardAgent: ResetApiState([]),
  resetDashboardAgent: ResetApiState(null),

  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const getDashboard = createAsyncThunk<Dashboard, any>(
    `/get-Dashboard`,
    async (data: any, thunkApi) =>
      axios
        .get(`${name}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDashboard = createAction(`/reset-Dashboard`);

  const getDashboardCategory = createAsyncThunk<DashboardCategory, any>(
    `/get-DashboardCategory`,
    async (data: any, thunkApi) =>
      axios
        .get(`${name}/catagory`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDashboardCategory = createAction(`/reset-dashboard-Category`);

  const getDashboardBank = createAsyncThunk<DashboardBank[], any>(
    `/get-DashboardBank`,
    async (data: any, thunkApi) =>
      axios
        .get(`${name}/banks`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDashboardBank = createAction(`/reset-dashboard-banks`);

  const getDashboardConductor = createAsyncThunk<DashboardConductor, any>(
    `/get-DashboardConductor`,
    async (data: any, thunkApi) =>
      axios
        .get(`${name}/conductors`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDashboardConductor = createAction(`/reset-conductors`);

  const getDashboardAgent = createAsyncThunk<DashboardAgent, any>(
    `/get-DashboardAgent`,
    async (data: any, thunkApi) =>
      axios
        .get(`${name}/agents`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDashboardAgent = createAction(`/reset-agents`);

  const resetStates = createAction(`/reset-states`);

  return {
    getDashboard,
    resetDashboard,

    getDashboardCategory,
    resetDashboardCategory,

    getDashboardBank,
    resetDashboardBank,

    getDashboardConductor,
    resetDashboardConductor,

    getDashboardAgent,
    resetDashboardAgent,

    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<ReportStateTypes>
) {
  return {
    ...getDashboard(),
    ...resetDashboard(),

    ...getDashboardCategory(),
    ...resetDashboardCategory(),

    ...getDashboardBank(),
    ...resetDashboardBank(),

    ...getDashboardConductor(),
    ...resetDashboardConductor(),

    ...getDashboardAgent(),
    ...resetDashboardAgent(),

    ...resetStatesReducer(),
  };

  function getDashboard() {
    return {
      ...builder.addCase(extraActions.getDashboard.pending, (state, action) => {
        state.dashboard = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getDashboard.fulfilled,
        (state, action) => {
          state.dashboard = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboard.rejected,
        (state, action) => {
          state.dashboard = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDashboard() {
    return {
      ...builder.addCase(extraActions.resetDashboard, (state, action) => {
        state.resetDashboard = ResetApiState(true);
        state.dashboard = ResetApiState(null);
      }),
    };
  }

  function getDashboardCategory() {
    return {
      ...builder.addCase(
        extraActions.getDashboardCategory.pending,
        (state, action) => {
          state.dashboardCategory = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardCategory.fulfilled,
        (state, action) => {
          state.dashboardCategory = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardCategory.rejected,
        (state, action) => {
          state.dashboardCategory = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDashboardCategory() {
    return {
      ...builder.addCase(
        extraActions.resetDashboardCategory,
        (state, action) => {
          state.resetDashboardCategory = ResetApiState(true);
          state.dashboardCategory = ResetApiState(null);
        }
      ),
    };
  }

  function getDashboardBank() {
    return {
      ...builder.addCase(
        extraActions.getDashboardBank.pending,
        (state, action) => {
          state.dashboardBank = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardBank.fulfilled,
        (state, action) => {
          state.dashboardBank = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardBank.rejected,
        (state, action) => {
          state.dashboardBank = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDashboardBank() {
    return {
      ...builder.addCase(extraActions.resetDashboardBank, (state, action) => {
        state.resetDashboardBank = ResetApiState(true);
        state.dashboardBank = ResetApiState(null);
      }),
    };
  }

  function getDashboardConductor() {
    return {
      ...builder.addCase(
        extraActions.getDashboardConductor.pending,
        (state, action) => {
          state.dashboardConductor = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardConductor.fulfilled,
        (state, action) => {
          state.dashboardConductor = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardConductor.rejected,
        (state, action) => {
          state.dashboardConductor = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDashboardConductor() {
    return {
      ...builder.addCase(
        extraActions.resetDashboardConductor,
        (state, action) => {
          state.resetDashboardConductor = ResetApiState(true);
          state.dashboardConductor = ResetApiState(null);
        }
      ),
    };
  }

  function getDashboardAgent() {
    return {
      ...builder.addCase(
        extraActions.getDashboardAgent.pending,
        (state, action) => {
          state.dashboardAgent = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardAgent.fulfilled,
        (state, action) => {
          state.dashboardAgent = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDashboardAgent.rejected,
        (state, action) => {
          state.dashboardAgent = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDashboardAgent() {
    return {
      ...builder.addCase(extraActions.resetDashboardAgent, (state, action) => {
        state.resetDashboardAgent = ResetApiState(true);
        state.dashboardAgent = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.dashboard = ResetApiState(null);
        state.dashboardCategory = ResetApiState(null);
        state.dashboardBank = ResetApiState([]);
        state.dashboardConductor = ResetApiState([]);
        state.dashboardAgent = ResetApiState([]);
      }),
    };
  }
}

const DashboardSlice = createSlice({
  name,
  initialState: ReportInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const DashboardActions = {
  ...DashboardSlice.actions,
  ...extraActions,
};
export const DashboardReducer = DashboardSlice.reducer;
